import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Botao, Chip, ErrorState, EmptyState, Modal, Nota, Recolhivel, Skeleton } from '../../../ui';
import { EnvioLogItem, fetchEnviosLog } from '../../../api/sga-envio-log/sgaEnvioLogApi';
import { extrairMensagemErro } from '../../../utils/apiError';
import { formatarDataHora } from '../../../utils/data';
import {
  rotuloOrigemLog,
  rotuloStatusLog,
  rotuloTipoLog,
} from '../../../utils/envioSgaRegras';

interface ModalInformacoesEnviadasProps {
  isOpen: boolean;
  onClose: () => void;
  /** Filtro por registro: use cpfCnpj para associado ou placa para veículo/vistoria. */
  filtro: { cpfCnpj?: string; placa?: string };
  titulo?: string;
}

/**
 * Histórico do que foi enviado ao SGA (at-11; reescrito na at-70, frente D/D8).
 *
 * Mostra, por registro, cada tentativa de envio: o payload exato transmitido, a
 * resposta do SGA e o desfecho. É o "Ver envio" da tela de Acompanhamento.
 *
 * ## O que mudou na at-70
 *
 *   1. 🐛 **Falha de carga era muda.** O `catch` dava `toast.error` (que passa) e a
 *      lista caía no texto *"Nenhum envio registrado para este item até o
 *      momento."* — que **fica**. O TI concluía que o registro nunca foi enviado,
 *      quando o que houve foi a consulta falhar. Agora são estados distintos, e o
 *      erro tem "Tentar novamente".
 *   2. 🔴 **Saíram os pictogramas**: o `📋` do título, o `ℹ️` do aviso, o `✕` do
 *      fechar e o `Detalhes ▼` / `Recolher ▲` de cada item (regra de Victor,
 *      06/09/2026). O fechar é o do `Modal`; o acordeão é o `Recolhivel`, que dá
 *      foco, Enter/Espaço e o anúncio de "expandido" de graça.
 *   3. 🔴 **Os valores crus do banco viraram texto de gente**: `VISTORIA_FOTOS`,
 *      `IMEDIATO` e `SUCESSO` eram exibidos como estão no enum. A tradução vive em
 *      `utils/envioSgaRegras.ts`.
 *   4. **Virou o `Modal` do kit**: foco preso, Esc, clique fora e título `<h2>`. O
 *      overlay próprio (`.info-envio-overlay`, z-index 1000) e o listener de
 *      `mousedown` saíram, junto com o CSS inteiro do arquivo.
 *   5. `useSelector(accessToken)` como guarda saiu (redundante desde a at-17) —
 *      era ele que fazia o modal abrir **vazio e mudo** quando o token ainda não
 *      tinha sido lido do store.
 *   6. O botão "Fechar" do rodapé saiu: ele usava `.usuarios-btn secondary`, classe
 *      que **não existe em CSS nenhum** desde a at-68 (estava cru na tela), e o
 *      `Modal` já tem o fechar no cabeçalho.
 *
 * ⚠️ **As props seguem em inglês** (`isOpen`/`onClose`/`filtro`/`titulo`): são o
 * contrato de dois chamadores na tela de envios, e renomeá-las é trabalho
 * transversal, não desta branch (mesma razão dos modais de escolha, at-59).
 *
 * ⚠️ **A data perdeu os SEGUNDOS** (era `toLocaleString` com `second: '2-digit'`).
 * O formato do app é `formatarDataHora` (at-66), e dois envios no mesmo minuto
 * continuam distinguíveis pela ordem e pelo conteúdo do payload.
 */
const ModalInformacoesEnviadas: React.FC<ModalInformacoesEnviadasProps> = ({
  isOpen,
  onClose,
  filtro,
  titulo,
}) => {
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [erro, setErro] = useState('');
  const [itens, setItens] = useState<EnvioLogItem[]>([]);

  const { cpfCnpj, placa } = filtro;

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await fetchEnviosLog({ cpfCnpj, placa, limit: 100 });
      setItens(Array.isArray(resposta?.items) ? resposta.items : []);
      setErro('');
      setEstado('ok');
    } catch (falha) {
      // Lista descartada: dado de outro registro passando por este seria pior.
      setItens([]);
      setErro(extrairMensagemErro(falha, ''));
      setEstado('erro');
    }
  }, [cpfCnpj, placa]);

  useEffect(() => {
    if (!isOpen) return;
    void carregar();
  }, [isOpen, carregar]);

  const copiar = (dados: unknown, rotulo: string) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(dados, null, 2));
      toast.success(`${rotulo} copiado.`);
    } catch {
      toast.error('Não foi possível copiar para a área de transferência.');
    }
  };

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={3} altura="tw-h-16" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o histórico de envios."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (itens.length === 0) {
      return (
        <EmptyState
          icone="enviar"
          titulo="Nenhum envio registrado para este item"
          descricao="Nada foi transmitido ao SGA para este registro até agora — ou o envio é anterior à ativação desta auditoria."
        />
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        {itens.map((item) => (
          <ItemDoLog key={item.id} item={item} aoCopiar={copiar} />
        ))}
      </div>
    );
  };

  return (
    <Modal
      aberto={isOpen}
      titulo="Informações enviadas ao SGA"
      descricao={titulo}
      tamanho="lg"
      aoFechar={onClose}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Nota>
          Este histórico mostra o que foi <strong className="tw-font-bold">efetivamente
          transmitido</strong> ao SGA no momento da operação — payload e resposta. Envios anteriores
          à ativação desta auditoria não foram capturados.
        </Nota>

        {conteudo()}
      </div>
    </Modal>
  );
};

/**
 * Uma tentativa de envio.
 *
 * 📌 O resumo do `Recolhivel` traz o desfecho: sem abrir, o TI já sabe se aquele
 * envio deu certo. Era o que os três badges faziam, com o status cru.
 */
const ItemDoLog: React.FC<{
  item: EnvioLogItem;
  aoCopiar: (dados: unknown, rotulo: string) => void;
}> = ({ item, aoCopiar }) => {
  const status = rotuloStatusLog(item.status);

  return (
    <Recolhivel
      titulo={formatarDataHora(item.criadoEm)}
      resumo={
        <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <Chip variante="neutro">{rotuloTipoLog(item.tipo)}</Chip>
          {item.acao && <Chip variante="neutro">{item.acao}</Chip>}
          <Chip variante={status.variante}>{status.texto}</Chip>
        </span>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-texto-suave">
          {item.volunteerCode && (
            <span>
              Código do voluntário:{' '}
              <strong className="tw-font-bold tw-text-texto-corpo">{item.volunteerCode}</strong>
            </span>
          )}
          {item.codigoSga && (
            <span>
              Código no SGA:{' '}
              <strong className="tw-font-bold tw-text-texto-corpo">{item.codigoSga}</strong>
            </span>
          )}
          <span>
            Disparo:{' '}
            <strong className="tw-font-bold tw-text-texto-corpo">
              {rotuloOrigemLog(item.origem)}
            </strong>
          </span>
        </div>

        {item.erro && (
          <p className="tw-m-0 tw-rounded-lg tw-bg-danger-bg tw-px-3 tw-py-2 tw-text-xs tw-text-danger">
            <strong className="tw-font-bold">Erro retornado:</strong> {item.erro}
          </p>
        )}

        <BlocoJson
          titulo="Payload enviado"
          dados={item.payloadEnviado}
          aoCopiar={() => aoCopiar(item.payloadEnviado, 'Payload')}
        />

        {item.respostaSga != null && (
          <BlocoJson
            titulo="Resposta do SGA"
            dados={item.respostaSga}
            aoCopiar={() => aoCopiar(item.respostaSga, 'Resposta do SGA')}
          />
        )}
      </div>
    </Recolhivel>
  );
};

const BlocoJson: React.FC<{
  titulo: string;
  dados: unknown;
  aoCopiar: () => void;
}> = ({ titulo, dados, aoCopiar }) => (
  <div className="tw-flex tw-flex-col tw-gap-1.5">
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-suave">
        {titulo}
      </span>
      <Botao variante="fantasma" tamanho="sm" onClick={aoCopiar}>
        Copiar
      </Botao>
    </div>

    <pre className="tw-m-0 tw-max-h-60 tw-overflow-auto tw-rounded-lg tw-bg-texto-titulo tw-p-3 tw-font-mono tw-text-[11px] tw-leading-relaxed tw-text-white">
      {JSON.stringify(dados, null, 2)}
    </pre>
  </div>
);

export default ModalInformacoesEnviadas;
